import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../services/api';
import { Pool, PoolTeam, PICK6_ROUND_MULTIPLIERS } from '../../types/pool';

interface Pick6Entry {
  id: string;
  entryName: string;
  userId: string;
  displayName: string;
  score: number;
  teams: Array<{
    teamId: string;
    teamName: string;
    seed: number;
    region?: string;
  }>;
}

// Removed unused Pick6Status interface - using direct fetch for entries

const ROUNDS = [
  'Round of 64',
  'Round of 32',
  'Sweet 16',
  'Elite 8',
  'Final Four',
  'Championship',
];

export function AdminPick6Results() {
  const { poolId } = useParams<{ poolId: string }>();
  const { getAccessTokenSilently } = useAuth0();

  const [pool, setPool] = useState<Pool | null>(null);
  const [teams, setTeams] = useState<PoolTeam[]>([]);
  const [entries, setEntries] = useState<Pick6Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Results entry state
  const [selectedRound, setSelectedRound] = useState<string>('Round of 64');
  const [advancingTeams, setAdvancingTeams] = useState<Set<string>>(new Set());
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<string | null>(null);

  // Track which rounds have been processed
  const [completedRounds, setCompletedRounds] = useState<Record<string, string[]>>({});

  const loadData = useCallback(async () => {
    if (!poolId) return;

    try {
      setLoading(true);
      setError(null);

      // Load pool details
      const poolData = await api.getPool(poolId);
      setPool(poolData);
      setTeams(poolData.teams || []);

      // Load entries with their picks
      const entriesData = await api.getPoolEntries(poolId);
      const mappedEntries: Pick6Entry[] = entriesData.map((e: any) => ({
        id: e.id,
        entryName: e.entryName || 'Unnamed Entry',
        userId: e.userId,
        displayName: e.displayName || e.username || 'Unknown',
        score: parseFloat(e.score) || 0,
        teams: e.picks?.teams || [],
      }));
      setEntries(mappedEntries);

      // Try to load existing round results from pool metadata or a separate endpoint
      // For now, we'll track them locally
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [poolId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Group teams by region
  const teamsByRegion = useMemo(() => {
    const grouped: Record<string, PoolTeam[]> = {};
    for (const team of teams) {
      const region = team.metadata?.region || 'Other';
      if (!grouped[region]) {
        grouped[region] = [];
      }
      grouped[region].push(team);
    }
    // Sort by seed
    for (const region of Object.keys(grouped)) {
      grouped[region].sort((a, b) => (a.seed || 16) - (b.seed || 16));
    }
    return grouped;
  }, [teams]);

  function toggleTeamAdvancing(teamId: string) {
    setAdvancingTeams(prev => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  }

  async function handleProcessRound() {
    if (!poolId || advancingTeams.size === 0) return;

    try {
      setProcessing(true);
      setProcessResult(null);
      const token = await getAccessTokenSilently();

      const result = await api.processPick6Results(
        poolId,
        { round: selectedRound, advancingTeams: Array.from(advancingTeams) },
        token,
      );

      setProcessResult(`${selectedRound} results processed! ${result.stats?.entriesProcessed || 0} entries updated, ${result.stats?.totalPointsAwarded || 0} points awarded.`);

      // Track completed round
      setCompletedRounds(prev => ({
        ...prev,
        [selectedRound]: Array.from(advancingTeams),
      }));

      // Reload data to get updated scores
      await loadData();

      // Clear selections
      setAdvancingTeams(new Set());
    } catch (err) {
      console.error('Failed to process results:', err);
      setProcessResult(`Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setProcessing(false);
    }
  }

  // Calculate potential points for an entry
  function calculatePotentialPoints(entry: Pick6Entry): number {
    return entry.teams.reduce((total, team) => {
      const maxMultiplier = Object.values(PICK6_ROUND_MULTIPLIERS).reduce((a, b) => a + b, 0);
      return total + (team.seed * maxMultiplier);
    }, 0);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !pool) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="font-medium">Error</p>
          <p className="text-sm mt-1">{error || 'Failed to load pool'}</p>
          <Link to="/admin" className="mt-3 inline-block text-sm text-blue-600 hover:underline">
            ← Back to Admin Dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Sort entries by score (descending)
  const sortedEntries = [...entries].sort((a, b) => b.score - a.score);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link to="/admin" className="text-blue-600 hover:underline text-sm">
          ← Back to Admin Dashboard
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 mt-2">{pool.name}</h1>
        <p className="text-gray-600">Pick 6 Pool Results Management</p>
      </div>

      {/* Scoring Reference */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Scoring: Seed × Round Multiplier</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          {Object.entries(PICK6_ROUND_MULTIPLIERS).map(([round, multiplier]) => (
            <div key={round} className={`px-3 py-1 rounded ${
              completedRounds[round] ? 'bg-green-100 text-green-800' : 'bg-white text-blue-800'
            }`}>
              <span className="font-medium">{round}:</span> ×{multiplier}
              {completedRounds[round] && ' ✓'}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Enter Round Results */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Enter Round Results</h2>
            <p className="text-sm text-gray-600 mt-1">Select teams that advanced to the next round</p>
          </div>

          <div className="px-6 py-4">
            {/* Round Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Round
              </label>
              <select
                value={selectedRound}
                onChange={(e) => {
                  setSelectedRound(e.target.value);
                  setAdvancingTeams(new Set());
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ROUNDS.map(round => (
                  <option key={round} value={round}>
                    {round} {completedRounds[round] ? '(Completed)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Team Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Teams That Advanced ({advancingTeams.size} selected)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Click teams that won and advanced to the next round
              </p>

              {Object.entries(teamsByRegion).map(([region, regionTeams]) => (
                <div key={region} className="mb-4">
                  <h4 className="text-sm font-medium text-gray-600 mb-2">{region}</h4>
                  <div className="flex flex-wrap gap-2">
                    {regionTeams.map((team) => {
                      const isSelected = advancingTeams.has(team.team.id);
                      return (
                        <button
                          key={team.team.id}
                          onClick={() => toggleTeamAdvancing(team.team.id)}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                            isSelected
                              ? 'bg-green-100 text-green-800 border-2 border-green-500'
                              : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                          }`}
                        >
                          ({team.seed}) {team.team.shortName || team.team.name}
                          {isSelected && ' ✓'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Teams Summary */}
            {advancingTeams.size > 0 && (
              <div className="mb-4 p-3 bg-green-50 rounded-lg">
                <h4 className="text-sm font-medium text-green-800 mb-2">
                  Advancing Teams ({advancingTeams.size}):
                </h4>
                <div className="flex flex-wrap gap-1 text-sm text-green-700">
                  {teams
                    .filter(t => advancingTeams.has(t.team.id))
                    .map(t => (
                      <span key={t.team.id} className="bg-green-100 px-2 py-0.5 rounded">
                        ({t.seed}) {t.team.shortName || t.team.name}
                      </span>
                    ))}
                </div>
              </div>
            )}

            {/* Process Button */}
            <button
              onClick={handleProcessRound}
              disabled={processing || advancingTeams.size === 0}
              className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing && (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {processing ? 'Processing...' : `Process ${selectedRound} Results`}
            </button>

            {/* Result Message */}
            {processResult && (
              <div className={`mt-4 p-3 rounded-lg text-sm ${
                processResult.startsWith('Error')
                  ? 'bg-red-50 text-red-800'
                  : 'bg-green-50 text-green-800'
              }`}>
                {processResult}
              </div>
            )}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Leaderboard</h2>
            <p className="text-sm text-gray-600 mt-1">{entries.length} entries</p>
          </div>

          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {sortedEntries.length === 0 ? (
              <div className="px-6 py-4 text-gray-500 text-sm">No entries yet</div>
            ) : (
              sortedEntries.map((entry, index) => (
                <div key={entry.id} className="px-6 py-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-800' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{entry.entryName}</div>
                        <div className="text-sm text-gray-500">{entry.displayName}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-blue-600">{entry.score}</div>
                      <div className="text-xs text-gray-500">
                        Max: {calculatePotentialPoints(entry)}
                      </div>
                    </div>
                  </div>
                  {/* Entry's Teams */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {entry.teams.map((team) => (
                      <span
                        key={team.teamId}
                        className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600"
                      >
                        ({team.seed}) {team.teamName}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="font-semibold text-yellow-800 mb-2">How Scoring Works</h3>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• Each entry selected 6 teams before the tournament started</li>
          <li>• When a selected team wins a game, the entry earns: <strong>Seed × Round Multiplier</strong></li>
          <li>• Example: A 12-seed winning Round of 64 = 12 × 1 = 12 points</li>
          <li>• A 1-seed winning Championship = 1 × 11 = 11 points</li>
          <li>• Process results for each round as games complete</li>
        </ul>
      </div>
    </div>
  );
}
